import { useNavigate } from 'react-router-dom'
import { Lightbulb, AlertTriangle, CheckCircle2, Info, CircleDot, CalendarClock } from 'lucide-react'
import type { Account, Dataset, EntityRef, InsightDriver } from '@/types/entities'
import {
  SectionCard, EmptyState, Badge, HealthBadge, KpiTile, KpiGrid,
} from '@/components/ui'
import { date, daysUntil, num } from '@/lib/format'
import type { AccountDerived } from '../lib'
import { priorityTone, urgencyTone, keyDatesForAccount } from '../lib'

const DRIVER_ICON: Record<InsightDriver['tone'], React.ReactNode> = {
  ok: <CheckCircle2 size={16} />,
  warn: <AlertTriangle size={16} />,
  alert: <AlertTriangle size={16} />,
  neutral: <Info size={16} />,
}

export default function InsightsTab({ account, d, data }: { account: Account; d: AccountDerived; data: Dataset }) {
  const navigate = useNavigate()
  const insight = d.insight

  if (!insight) {
    return (
      <SectionCard pad>
        <EmptyState
          icon={<Lightbulb size={36} />}
          title="No WISE assessment for this account yet"
          hint="WISE generates a diagnostic verdict, evidence-led drivers, and recommended actions once enough operating data has accrued. This account has not yet been scored in the prototype."
        />
      </SectionCard>
    )
  }

  const refTarget = (ref?: EntityRef) => {
    if (!ref) return undefined
    if (ref.kind === 'property') return () => navigate(`/properties/${ref.id}`)
    if (ref.kind === 'contact') return () => navigate(`/contacts/${ref.id}`)
    if (ref.kind === 'fund') return () => navigate(`/funds/${ref.id}`)
    return undefined
  }

  const propIds = d.propIds
  const keyDates = keyDatesForAccount(data, account.id, propIds)
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : 1))

  // Open-item aging buckets from this account's open items.
  const today = '2026-06-13'
  const ageOf = (createdAt?: string) => {
    if (!createdAt) return 0
    const diff = (new Date(`${today}T00:00:00`).getTime() - new Date(`${createdAt}T00:00:00`).getTime()) / 86_400_000
    return Math.max(0, Math.round(diff))
  }
  const overdueCount = d.openItems.filter((i) => {
    const du = daysUntil(i.dueDate)
    return du != null && du < 0
  }).length

  return (
    <div className="aw-grid-2col">
      <div className="aw-col-main">
        <SectionCard eyebrow="WISE Assessment" title={<>Diagnostic <b>Verdict</b></>}>
          <div className="aw-verdict-banner">
            <div className={`aw-verdict-chip aw-verdict-chip--${insight.verdict === 'Healthy' ? 'ok' : insight.verdict === 'Watch' ? 'warn' : 'alert'}`}>
              <HealthBadge health={insight.verdict} />
            </div>
            <p className="aw-narrative">{insight.narrative}</p>
          </div>
        </SectionCard>

        <SectionCard eyebrow="Evidence" title={<>Key <b>Drivers</b></>}>
          <ul className="aw-drivers">
            {insight.drivers.map((dr, i) => {
              const onClick = refTarget(dr.ref)
              return (
                <li key={i} className={`aw-driver aw-driver--${dr.tone}`}>
                  <span className="aw-driver-icon">{DRIVER_ICON[dr.tone]}</span>
                  <div className="aw-driver-body">
                    <div className="aw-driver-head">
                      <span className="aw-driver-label">{dr.label}</span>
                      {onClick && <button className="aw-driver-ref" onClick={onClick}>View →</button>}
                    </div>
                    <p className="aw-driver-detail">{dr.detail}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        </SectionCard>

        <SectionCard eyebrow="Next Best Actions" title={<>Recommended <b>Actions</b></>}>
          <ul className="aw-recs">
            {insight.recommended.map((r, i) => (
              <li key={i} className="aw-rec">
                <span className="aw-rec-num num">{i + 1}</span>
                <div className="aw-rec-body">
                  <div className="aw-rec-head">
                    <span className="aw-rec-title">{r.title}</span>
                    {r.priority && <Badge tone={priorityTone(r.priority)} variant="soft">{r.priority}</Badge>}
                  </div>
                  {r.rationale && <p className="aw-rec-rationale">{r.rationale}</p>}
                  {r.suggestedOwner && <div className="aw-rec-owner label">Owner · {r.suggestedOwner}</div>}
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="aw-col-side">
        <SectionCard eyebrow="Open Items" title={<>Item <b>Aging</b></>}>
          <KpiGrid cols={2}>
            <KpiTile label="Open" value={num(insight.openItems?.total ?? d.openItems.length)} />
            <KpiTile label="Overdue" value={num(insight.openItems?.overdue ?? overdueCount)} accent />
          </KpiGrid>
          {insight.openItems && (
            <div className="aw-aging-meta">
              <div className="aw-meta-row"><span className="label">Avg Age</span><span className="num">{insight.openItems.avgAgeDays}d</span></div>
              {insight.openItems.oldest && (
                <div className="aw-meta-row aw-meta-row--col">
                  <span className="label">Oldest Open</span>
                  <span className="aw-meta-val">{insight.openItems.oldest}</span>
                </div>
              )}
            </div>
          )}
          {!insight.openItems && d.openItems.length > 0 && (
            <ul className="aw-mini-list">
              {d.openItems.slice(0, 4).map((i) => (
                <li key={i.id} className="aw-mini-item">
                  <CircleDot size={12} />
                  <span className="truncate">{i.title}</span>
                  <span className="aw-mini-meta">{ageOf(i.createdAt)}d</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard eyebrow="Radar" title={<>Critical <b>Dates</b></>}>
          {keyDates.length ? (
            <ul className="aw-radar">
              {keyDates.map((k) => {
                const du = daysUntil(k.date)
                return (
                  <li key={k.id} className="aw-radar-item">
                    <span className="aw-radar-icon"><CalendarClock size={15} /></span>
                    <div className="aw-radar-body">
                      <span className="aw-radar-title truncate">{k.title}</span>
                      <span className="aw-radar-sub">{k.type}{k.owner ? ` · ${k.owner}` : ''}</span>
                    </div>
                    <div className="aw-radar-meta">
                      <Badge tone={urgencyTone(k.status)} variant="soft">{k.status}</Badge>
                      <span className="aw-radar-date">{date(k.date, 'short')}{du != null && <span className="aw-radar-days">{du >= 0 ? `in ${du}d` : `${-du}d ago`}</span>}</span>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <EmptyState icon={<CalendarClock size={28} />} title="No critical dates" hint="Reporting windows, valuations, and maturities will appear here." />
          )}
        </SectionCard>
      </div>
    </div>
  )
}
